import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ShoppingBag } from 'lucide-react';
import { useCart } from '../context/CartContext';

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { setOpen: openCart, totalQty } = useCart();

  // onScroll is defined inside the effect; setScrolled is a stable state setter.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // setMobileOpen is a stable state setter; location is the only reactive dep.
  useEffect(() => { setMobileOpen(false); }, [location]);

  const navLinks = [
    { label: 'Shop', to: '/shop' },
    { label: 'About', to: '/about' },
    { label: 'Blog', to: '/blog' },
    { label: 'Contact', to: '/contact' },
  ];

  return (
    <div className="relative z-50">
      {/* Announcement Bar */}
      <div
        data-testid="announcement-bar"
        style={{ backgroundColor: '#1B3D7E', color: '#ffffff', textAlign: 'center', padding: '10px 16px', fontSize: '0.7rem', letterSpacing: '0.2em', textTransform: 'uppercase', fontFamily: 'Manrope, sans-serif', fontWeight: 600 }}
        className="!text-[#FFFFFF] !bg-[#1B3D7E]">
        Free UK Delivery Over £30 &nbsp;|&nbsp; Natural Plant-Based Male Performance Formula
      </div>

      {/* Main Nav */}
      <header
        data-testid="main-header"
        className="sticky top-0 z-50 transition-all duration-300"
        style={{
          backgroundColor: '#FFFFFF',
          borderBottom: '1px solid #E2E8F0',
          boxShadow: scrolled ? '0 2px 20px rgba(13,26,46,0.08)' : 'none',
        }}>

        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" data-testid="logo-link" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 800, fontSize: '1.4rem', letterSpacing: '0.25em', color: '#0D1A2E', textDecoration: 'none', textTransform: 'uppercase' }}>
            RE<span style={{ color: '#1B3D7E' }}>LOAD</span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                style={{ fontFamily: 'Manrope, sans-serif', fontSize: '0.78rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: location.pathname === link.to ? '#1B3D7E' : '#64748B', textDecoration: 'none', fontWeight: 600, transition: 'color 0.2s' }}
                onMouseEnter={(e) => e.target.style.color = '#1B3D7E'}
                onMouseLeave={(e) => e.target.style.color = location.pathname === link.to ? '#1B3D7E' : '#64748B'}>
                {link.label}
              </Link>
            ))}
            <a
              href="/#faq"
              style={{ fontFamily: 'Manrope, sans-serif', fontSize: '0.78rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: '#64748B', textDecoration: 'none', fontWeight: 600, transition: 'color 0.2s' }}
              onMouseEnter={(e) => e.target.style.color = '#1B3D7E'}
              onMouseLeave={(e) => e.target.style.color = '#64748B'}>
              FAQ
            </a>
          </nav>

          {/* Desktop CTAs */}
          <div className="hidden md:flex items-center gap-3">
            {/* Cart icon */}
            <button
              data-testid="header-cart-btn"
              onClick={() => openCart(true)}
              style={{ position: 'relative', background: 'none', border: 'none', cursor: 'pointer', padding: '6px', color: '#0D1A2E', display: 'flex', alignItems: 'center', transition: 'color 0.2s' }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#1B3D7E'}
              onMouseLeave={(e) => e.currentTarget.style.color = '#0D1A2E'}
            >
              <ShoppingBag size={20} strokeWidth={1.5} />
              {totalQty > 0 && (
                <span style={{ position: 'absolute', top: '0', right: '0', backgroundColor: '#1B3D7E', color: '#FFFFFF', fontSize: '0.6rem', fontFamily: 'Manrope, sans-serif', fontWeight: 700, borderRadius: '100px', minWidth: '16px', height: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px' }}>
                  {totalQty}
                </span>
              )}
            </button>
            <Link
              to="/shop"
              data-testid="header-cta-btn"
              className="btn-blue gap-2 px-6 py-2.5 rounded-full text-xs"
              style={{ fontFamily: 'Manrope, sans-serif', textDecoration: 'none' }}>
              Buy Now — £24.99
            </Link>
          </div>

          {/* Mobile: cart icon + hamburger */}
          <div className="md:hidden flex items-center gap-2">
            <button
              data-testid="mobile-cart-btn"
              onClick={() => openCart(true)}
              style={{ position: 'relative', background: 'none', border: 'none', cursor: 'pointer', padding: '6px', color: '#0D1A2E', display: 'flex', alignItems: 'center' }}
            >
              <ShoppingBag size={20} strokeWidth={1.5} />
              {totalQty > 0 && (
                <span style={{ position: 'absolute', top: '0', right: '0', backgroundColor: '#1B3D7E', color: '#FFFFFF', fontSize: '0.6rem', fontFamily: 'Manrope, sans-serif', fontWeight: 700, borderRadius: '100px', minWidth: '16px', height: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px' }}>
                  {totalQty}
                </span>
              )}
            </button>
            <button onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-toggle" className="p-2" style={{ color: '#0D1A2E' }}>
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div data-testid="mobile-menu" className="md:hidden px-6 py-5 flex flex-col gap-4" style={{ backgroundColor: '#FFFFFF', borderTop: '1px solid #E2E8F0' }}>
            {navLinks.map((link) => (
              <Link key={link.to} to={link.to} style={{ fontFamily: 'Manrope, sans-serif', fontSize: '0.78rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: '#64748B', textDecoration: 'none', fontWeight: 600, padding: '8px 0' }}>
                {link.label}
              </Link>
            ))}
            <a href="/#faq" style={{ fontFamily: 'Manrope, sans-serif', fontSize: '0.78rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: '#64748B', textDecoration: 'none', fontWeight: 600, padding: '8px 0' }}>FAQ</a>
            <Link to="/shop" data-testid="mobile-cta-btn" className="btn-blue text-center py-3 rounded-full text-xs mt-1" style={{ fontFamily: 'Manrope, sans-serif', textDecoration: 'none' }}>
              Buy Now — £24.99
            </Link>
          </div>
        )}
      </header>
    </div>
  );
}